import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import Grid from "../../../components/Grid";
import { MoreHorizontal } from "lucide-react";
import { ShowBtn } from "@/components/Show";
import { CreateLoaderInput } from "../../../components/Loaders";
import { DeleteBtn } from "@/components/Delete";
import { TypeTableProps } from "@/App/Plans/Table";
import { cn } from "@/lib/utils";
import { colors } from "@/utils/colors";
import { ComponentSwitchToggleStatus } from "@/App/Courses/ActivedOrDisabled";
import { UpdateBtn } from "../Update";
import { TableDataProps, SellersSort } from "@/pages/Seller";
import { ArrowDown, ArrowUp, ArrowUpDown } from "lucide-react";
import {
  setStatusSellers,
  getShowSellers,
  deleteSellersData,
} from "@/Api/sellers";
import { Badge } from "@/components/ui/badge";
import { Mask } from "@/utils/mask";
import { usePermissions } from "@/hooks/usePermissions";
import { If } from "@/components/If";

type SellersTableProps = TypeTableProps<TableDataProps> & {
  sort: SellersSort;
  onSort: (column: SellersSort["order_by"]) => void;
};

export const TableData = ({
  dataApi,
  pageActions,
  updateFront,
  sort,
  onSort,
}: SellersTableProps) => {
  const { hasPermission } = usePermissions();
  return (
    <>
      <Table className="bg-zinc-50 rounded dark:bg-[#2c2c2c]">
        <TableHeader>
          <TableRow>
            {(
              [
                { column: "name", label: "Nome" },
                { column: "nickname", label: "Apelido" },
              ] as const
            ).map(({ column, label }) => (
              <TableHead key={column}>
                <button
                  type="button"
                  className="flex items-center gap-1 hover:text-foreground"
                  onClick={() => onSort(column)}
                >
                  {label}
                  {sort.order_by !== column ? (
                    <ArrowUpDown className="h-3.5 w-3.5 opacity-50" />
                  ) : sort.order_direction === "asc" ? (
                    <ArrowUp className="h-3.5 w-3.5" />
                  ) : (
                    <ArrowDown className="h-3.5 w-3.5" />
                  )}
                </button>
              </TableHead>
            ))}
            <TableHead>Email</TableHead>
            <TableHead>Unidade</TableHead>
            <TableHead className="text-center">Ativo</TableHead>
            <TableHead className="text-center">Ações</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {dataApi?.data.map((item, index) => {
            return (
              <TableRow key={item?.id}>
                <TableCell className="truncate max-w-80">
                  {item?.user?.name}
                </TableCell>
                <TableCell className="truncate max-w-80">
                  {item?.nickname || "-"}
                </TableCell>
                <TableCell className="truncate max-w-80">
                  {item?.user?.email}
                </TableCell>
                <TableCell className="truncate max-w-80">
                  {item?.unity?.name}
                </TableCell>

                <TableCell>
                  <ComponentSwitchToggleStatus
                    defaultChecked={Boolean(item?.is_active)}
                    params={{
                      func: setStatusSellers,
                      funcParams: item?.uuid,
                    }}
                    updateFront={() =>
                      updateFront((prev) => {
                        return {
                          ...prev,
                          data: prev?.data?.map((values) => {
                            if (values?.uuid === item?.uuid)
                              return {
                                ...values,
                                is_active: values.is_active ? 0 : 1,
                              };
                            return values;
                          }),
                        };
                      })
                    }
                  />
                </TableCell>
                <TableCell className="flex justify-center">
                  <div className="flex gap-3 w-full justify-center">
                    <If condition={hasPermission("edit-seller")}>
                      <UpdateBtn
                        uuid={item?.uuid}
                        updateFront={(response) => {
                          updateFront((prev) => {
                            prev?.data?.splice(index, 1, response);
                            return {
                              ...prev,
                              data: [...prev.data],
                            };
                          });
                        }}
                      />
                    </If>
                    <If condition={hasPermission("delete-seller")}>
                      <DeleteBtn
                        params={{
                          func: deleteSellersData,
                          funcParams: item?.uuid,
                        }}
                        updateFront={() =>
                          updateFront((prev) => ({
                            ...prev,
                            data: [
                              ...prev?.data?.filter(
                                (data) => data?.uuid !== item?.uuid
                              ),
                            ],
                          }))
                        }
                        referenceLabel={item?.user?.name}
                      />
                    </If>
                    <If condition={hasPermission("show-seller-details")}>
                      <ShowBtn
                        labels={{ title: "Visualizar Vendedor" }}
                        params={{
                          func: getShowSellers,
                          funcParams: item?.uuid,
                        }}
                        render={(responseApi: TableDataProps) => (
                          <div className="flex flex-col gap-4">
                            <div className="flex items-center justify-between gap-3 rounded-lg bg-primary/5 p-4">
                              <div className="min-w-0">
                                <p className="text-lg font-semibold truncate">
                                  {responseApi?.user?.name}
                                </p>
                                {responseApi?.nickname && (
                                  <p className="text-sm text-muted-foreground truncate">
                                    “{responseApi.nickname}”
                                  </p>
                                )}
                              </div>
                              <Badge
                                className={
                                  responseApi?.is_active
                                    ? "bg-green-100 text-green-800"
                                    : "bg-red-100 text-red-800"
                                }
                              >
                                {responseApi?.is_active ? "Ativo" : "Inativo"}
                              </Badge>
                            </div>
                            <Grid.Item name="Email" flex={0}>
                              <span className="break-all">
                                {responseApi?.user?.email || "Não informado"}
                              </span>
                            </Grid.Item>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                              <Grid.Item name="Unidade" flex={0}>
                                {responseApi?.unity?.name || "Não informada"}
                              </Grid.Item>
                              <Grid.Item name="Telefone" flex={0}>
                                {responseApi?.phone
                                  ? Mask.maskPhone(responseApi.phone)
                                  : "Não informado"}
                              </Grid.Item>
                              <Grid.Item name="Documento" flex={0}>
                                {responseApi?.document
                                  ? Mask.maskCpfOrCnpj(responseApi.document)
                                  : "Não informado"}
                              </Grid.Item>
                            </div>
                            <Grid.Item name="Endereço" flex={0}>
                              {responseApi?.address?.street
                                ? `${responseApi.address.street}, ${
                                    responseApi.address.number || "S/N"
                                  } - ${responseApi.address.neighborhood}, ${
                                    responseApi.address.city
                                  }/${responseApi.address.state}`
                                : "Não informado"}
                            </Grid.Item>
                          </div>
                        )}
                        loader={<CreateLoaderInput lines={4} />}
                      />
                    </If>
                  </div>
                </TableCell>
              </TableRow>
            );
          })}
          {dataApi?.data.length < 1 && (
            <TableRow>
              <TableCell colSpan={6}>Nada foi encontrado.</TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      <div className="flex justify-between mb-3">
        {dataApi?.data.length > 0 && (
          <span className="font-medium">
            Exibindo {dataApi?.current_page} de um total de {dataApi?.last_page}
          </span>
        )}
        {dataApi?.last_page > 1 && (
          <Pagination className="">
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious
                  href="#"
                  className={cn("h-8", colors.bg_primary)}
                  onClick={pageActions.previous}
                />
              </PaginationItem>
              <PaginationItem>
                <PaginationLink
                  href="#"
                  className={cn(
                    `size-8 bg-transparent border-2 border-[#023859] text-slate-900 font-bold cursor-auto hover:bg-transparent`
                  )}
                >
                  <span className="pointer-events-none dark:text-[#F4F4F5]">
                    {dataApi?.current_page}
                  </span>
                </PaginationLink>
              </PaginationItem>
              <PaginationItem>
                <PaginationLink
                  href="#"
                  className="text-black h-8 hover:bg-stone-950/10"
                  onClick={pageActions.lastPage}
                >
                  <MoreHorizontal className="h-4 w-4 dark:text-[#F4F4F5]" />
                </PaginationLink>
              </PaginationItem>
              <PaginationItem>
                <PaginationNext
                  href="#"
                  className={cn("h-8", colors.bg_primary)}
                  onClick={pageActions.next}
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        )}
      </div>
    </>
  );
};
